import getRestUrlUtils from "@gef-ui/utils/getRestUrl";
import compose from "ramda/es/compose";
import curry from "ramda/es/curry";
import identity from "ramda/es/identity";
import isFunction from "ramda-extension/es/isFunction";
import { CALL_API, CONTENT_TYPE_HEADER, METHOD, NOT_PROVIDED } from '../constants';
import { ARRAY_BUFFER_RESPONSE_TYPE } from '../middleware/reactiveApi/constants';
import { mergeWithResolveValueWithState, prepareMultipartData } from './utils';
/**
 * Curried api is used for composing specific api call
 *
 * @documented
 * @category api
 *
 * @param {METHOD} method
 * @param {object} {headers[{}], bodyManipulation[identity], responseType}
 * @param {object} {endpoint, body, actions[{}], additionalHeaders[{}], credentials, withCredentials,
 * 					responseManipulation[identity], actionName[not provided], runInBatch[false], notReportError[false],
 * 					errorManipulation[identity], customOkResponseResolver, operationUuid[actionName[NOT_PROVIDED]],
 * 					abortPreviousCall[false], retry, cancel, cache, meta, progressCallback}
 * @import import { curriedApi } from '@gef-ui/middleware-api/facade';
 * @return {Promise} promise
 */ export const curriedApi = curry((method, { headers = {}, bodyManipulation = identity, responseType }, { endpoint, body, actions = {}, additionalHeaders = {}, credentials, withCredentials, responseManipulation = identity, actionName = NOT_PROVIDED, runInBatch = false, notReportError = false, errorManipulation = identity, customOkResponseResolver, operationUuid = actionName, abortPreviousCall = false, progressCallback, retry, cancel, cache, meta, ...other })=>({
        [CALL_API]: {
            endpoint,
            method,
            headers: mergeWithResolveValueWithState(headers, additionalHeaders),
            actions,
            runInBatch,
            body: bodyManipulation(body),
            credentials,
            withCredentials,
            responseManipulation,
            customOkResponseResolver,
            errorManipulation,
            notReportError,
            actionName,
            operationUuid,
            abortPreviousCall,
            progressCallback,
            responseType,
            retry,
            cancel,
            cache,
            meta,
            ...other
        }
    }));
/**
 * Method = POST
 * Headers = application/x-www-form-urlencoded
 * BodyMethod = encodeURIComponent(JSON.stringify(body))
 *
 * @documented
 * @category api
 *
 * @param {object} {endpoint, body, actions[{}], additionalHeaders[{}], credentials}
 * @import import { postFormEncoded } from '@gef-ui/middleware-api/facade';
 * @return {Promise} promise
 */ export const postFormEncoded = curriedApi(METHOD.POST, {
    headers: CONTENT_TYPE_HEADER.APPLICATION_X_WWW_FORM_URLENCODED,
    bodyManipulation (body) {
        const manipulate = compose(encodeURIComponent, JSON.stringify);
        return isFunction(body) ? compose(manipulate, body) : manipulate(body);
    }
});
/**
 * Method = POST
 * Headers = multipart/form-data
 * BodyMethod = encodeURIComponent(JSON.stringify(body))
 *
 * @documented
 * @category api
 *
 * @param {object} {endpoint, body, actions[{}], additionalHeaders[{}], credentials}
 * @import import { postMultipartForm } from '@gef-ui/middleware-api/facade';
 * @return {Promise} promise
 */ export const postMultipartForm = curriedApi(METHOD.POST, {
    headers: CONTENT_TYPE_HEADER.MULTIPART_FORM_DATA,
    bodyManipulation (body) {
        return isFunction(body) ? compose(prepareMultipartData, body) : prepareMultipartData(body);
    }
});
const applicationJsonParams = {
    headers: CONTENT_TYPE_HEADER.APPLICATION_JSON,
    bodyManipulation (body) {
        return isFunction(body) ? compose(JSON.stringify, body) : JSON.stringify(body);
    }
};
/**
 * Method = POST
 * Headers = application/json
 * BodyMethod = JSON.stringify(body)
 *
 * @documented
 * @category api
 *
 * @param {object} {endpoint, body, actions[{}], additionalHeaders[{}], credentials}
 * @import import { postApplicationJson } from '@gef-ui/middleware-api/facade';
 * @return {Promise} promise
 */ export const postApplicationJson = curriedApi(METHOD.POST, applicationJsonParams);
/**
 * Method = POST
 * Headers = application/json
 * BodyMethod = JSON.stringify(body)
 * ResponseType = arraybuffer
 *
 * @documented
 * @category api
 *
 * @param {object} {endpoint, body, actions[{}], additionalHeaders[{}], credentials}
 * @import import { postApplicationJsonBinaryData } from '@gef-ui/middleware-api/facade';
 * @return {Promise} promise
 */ export const postApplicationJsonBinaryData = curriedApi(METHOD.POST, {
    ...applicationJsonParams,
    responseType: ARRAY_BUFFER_RESPONSE_TYPE
});
/**
 * Method = PUT
 * Headers = application/json
 * BodyMethod = JSON.stringify(body)
 *
 * @documented
 * @category api
 *
 * @param {object} {endpoint, body, actions[{}], additionalHeaders[{}], credentials}
 * @import import { putApplicationJson } from '@gef-ui/middleware-api/facade';
 * @return {Promise} promise
 */ export const putApplicationJson = curriedApi(METHOD.PUT, applicationJsonParams);
/**
 * Method = PATCH
 * Headers = application/json
 * BodyMethod = JSON.stringify(body)
 *
 * @documented
 * @category api
 *
 * @param {object} {endpoint, body, actions[{}], additionalHeaders[{}], credentials}
 * @import import { patchApplicationJson } from '@gef-ui/middleware-api/facade';
 * @return {Promise} promise
 */ export const patchApplicationJson = curriedApi(METHOD.PATCH, applicationJsonParams);
/**
 * Method = DELETE
 *
 * @documented
 * @category api
 *
 * @param {object} {endpoint, body, actions[{}], additionalHeaders[{}], credentials}
 * @import import { deleteData } from '@gef-ui/middleware-api/facade';
 * @return {Promise} promise
 */ export const deleteData = curriedApi(METHOD.DELETE, {});
/**
 * Method = POST
 *
 * @documented
 * @category api
 *
 * @param {object} {endpoint, body, actions[{}], additionalHeaders[{}], credentials}
 * @import import { postData } from '@gef-ui/middleware-api/facade';
 * @return {Promise} promise
 */ export const postData = curriedApi(METHOD.POST, {});
/**
 * Method = GET
 *
 * @documented
 * @category api
 *
 * @param {object} {endpoint, actions[{}], additionalHeaders[{}], credentials}
 * @import import { getData } from '@gef-ui/middleware-api/facade';
 * @return {Promise} promise
 */ export const getData = curriedApi(METHOD.GET, {});
/**
 * Method = GET
 *
 * @documented
 * @category api
 *
 * @param {object} {endpoint, actions[{}], additionalHeaders[{}], credentials}
 * @import import { getBinaryData } from '@gef-ui/middleware-api/facade';
 * @return {Promise} promise
 */ export const getBinaryData = curriedApi(METHOD.GET, {
    responseType: ARRAY_BUFFER_RESPONSE_TYPE
});
/**
 * Construct rest url
 *
 * @documented
 * @category api
 * @deprecated use this function from @gef-ui/utils
 *
 * @param {String} endpoint
 * @param {Object} urlData {pathVariables, routeParams, urlParams}
 * @import import { getRestUrl } from '@gef-ui/middleware-api/facade';
 * @return {String} endpoint url extended with path variables, route params and url params
 */ export const getRestUrl = getRestUrlUtils;
